import './Recommendation.scss';

import { CartAPI } from '@apis/cart';
import { RecommendationAPI } from '@apis/recommendation';
import type { RegistryComponentProps } from '@boost-sd/components-registry';
import { registryComponent } from '@boost-sd/components-registry/registry';
import type { Dict } from '@boost-sd/core-js';
import { useMountEffect } from '@boost-sd/core-js';
import type { ReactNodeRenderer } from '@components/CustomizableNode';
import LoadingIcon from '@components/LoadingIcon';
import RecommendationBundle from '@components/RecommendationBundle';
import Slider from '@components/Slider';
import type { ProductListItem } from '@providers/ProductListProvider';
import type { ProductItem as ProductItemType } from '@providers/ProductProvider';
import { getRecentlyViewedProducts } from '@providers/ProductProvider/recentlyViewedProduct';
import {
  createClsNameMap,
  isMobileWidth,
  mapModifiers,
  rebuildProductItemDataFromShopifyData,
  removeDefaultOptionsProducts,
  splitGoogleFontStyle,
} from '@utils';
import { omit, pick } from 'lodash-es';
import { useImperativeHandle, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import ProductItem from '../ProductItem';

export const clsNameMap = createClsNameMap({
  elements: {
    title: createClsNameMap({
      modifiers: ['left', 'center'],
    }),
    'title-text': createClsNameMap(),
  },
})('recommendation');

export type RecommendationWidgetProps = RegistryComponentProps<
  {
    widgetName: string;
    params: {
      limit?: number;
      shop: string;
      wid: string;
      recommendationType:
        | 'bestsellers'
        | 'newest-arrivals'
        | 'frequently-bought-together'
        | 'trending-products'
        | 'hand-picked-products'
        | 'recently-viewed'
        | 'most-viewed'
        | 'related-items';
      modelType?: 'FBT' | 'Complementary' | 'Alternative' | 'AIRelated';
      calculatedBasedOn?: 'view-events' | 'purchase-events';
      rangeOfTime?: '1-day' | '2-day' | '3-day' | '4-day' | '5-day' | '6-day' | '7-day';
      ruleBased?: Array<{
        type:
          | 'sameCollection'
          | 'sameProductType'
          | 'sameVendor'
          | 'sameTags'
          | 'sameMetafield'
          | 'sameProductCategory';

        status?: boolean;
        excluded?: string[];
        metafield?: string;
      }>;
    };
    designSettings?: {
      layoutDisplay?: 'carousel' | 'grid' | 'bundle';
      numberOfProductPerRow?: number;
      numberOfRecommendProduct?: number;
      bundleStyle?: 'style1' | 'style2';
      templateType?: 'default' | 'customization';
      themePreview?: string;
      titleAlignment?: 'center' | 'left';
      titleFont?: string;
      titleFontSize?: number;
      titleFontStyle?: string;
      titleTextColor?: string;
      titleTextTransform?: 'capitalize';
    };
    page: 'home' | 'collection' | 'product' | 'cart';
    onRender?: ReactNodeRenderer;
    product?: ProductListItem;
  },
  {
    getWidgetData: () => Promise<void>;
  }
>;

const RecommendationWidget = ({
  params,
  widgetName,
  designSettings = {
    layoutDisplay: 'carousel',
  },
  page,
  product,
  helpersRef,
}: RecommendationWidgetProps) => {
  const [state, setState] = useState<{
    products: ProductListItem[];
    loading: boolean;
  }>({
    products: [],
    loading: true,
  });

  const { ref, inView } = useInView({
    triggerOnce: true,
  });

  const autoAspectRatio = useMemo(() => {
    return state.products.reduce((maxRatio, currentProduct) => {
      const firstImage = currentProduct.images_info.length > 0 && currentProduct.images_info[0];
      if (!firstImage) return maxRatio;

      const { height, width } = firstImage;

      return Number(Math.max(height / width, maxRatio).toFixed(2));
    }, 0);
  }, [state.products]);

  const getWidgetData = async () => {
    const { recommendationType } = params;

    let productIds: number[] = [];
    let collectionId = null;

    if (page === 'product') {
      productIds = [window.boostSDAppConfig?.generalSettings?.product_id];
    }

    if (page === 'cart') {
      await CartAPI.getCartItems().then((res) => {
        productIds = res.items.map((item: Dict) => item.product_id);
      });
    }

    if (page === 'collection') {
      collectionId = window.boostSDAppConfig?.generalSettings?.collection_id;
    }

    if (recommendationType === 'recently-viewed') {
      productIds = getRecentlyViewedProducts();
    }

    if (
      productIds.length === 0 &&
      ['frequently-bought-together', 'related-items', 'recently-viewed'].includes(
        recommendationType
      )
    ) {
      setState({
        loading: false,
        products: [],
      });

      return;
    }

    RecommendationAPI.get(
      {
        ...pick(params, ['recommendationType', 'limit', 'shop', 'wid']),
      },
      {
        ...omit(params, ['tenantId']),
        productIds,
        collectionId,
      }
    )
      .then((res) => {
        let products = res.products.slice(0, designSettings.numberOfRecommendProduct || 12);

        /** REMOVE DEFAULT OPTION */
        products = removeDefaultOptionsProducts(products);

        setState({
          products,
          loading: false,
        });
      })
      .catch(() => {
        setState({
          loading: false,
          products: [],
        });
      });
  };

  useMountEffect(() => {
    getWidgetData();
  });

  const renderTitle = () => {
    // Split font style to font style and font weight
    const { titleFontStyle } = designSettings;
    const { fontWeight, fontStyle } = splitGoogleFontStyle(titleFontStyle || '');

    const titleStyle = {
      fontFamily: designSettings.titleFont,
      fontSize: designSettings.titleFontSize,
      fontStyle,
      fontWeight,
      color: designSettings.titleTextColor,
      textTransform: designSettings.titleTextTransform,
    };

    if (state.loading || !state.products.length) return;

    const { wid } = params;
    const recommendationTranslation = window.boostSDAppConfig?.translation?.recommendation || {};

    return (
      <div
        className={mapModifiers(
          clsNameMap.title,
          designSettings.titleAlignment && [designSettings.titleAlignment]
        )}
      >
        <span className={clsNameMap.elm('title-text')} style={titleStyle}>
          {recommendationTranslation[wid] ? recommendationTranslation[wid] : widgetName}
        </span>
      </div>
    );
  };

  const renderWidget = () => {
    if (designSettings.layoutDisplay === 'carousel' && !state.loading && !!state.products.length) {
      const defaultProductPerRow = 4;
      let slidesToShow = isMobileWidth()
        ? 2
        : designSettings?.numberOfProductPerRow || defaultProductPerRow;
      if (state.products.length < slidesToShow) {
        slidesToShow = state.products.length;
      }

      const sliderWidth = isMobileWidth()
        ? undefined
        : `${Math.min(slidesToShow / defaultProductPerRow, 1) * 100}%`;

      return (
        <Slider
          fixedButtonPosition
          hasPagination={state.products.length > slidesToShow}
          style={{
            width: sliderWidth,
          }}
          slidesToShow={slidesToShow}
          enableButtons={state.products.length > slidesToShow}
          slides={state.products}
          onSlideRender={(product) => (
            <ProductItem
              product={product as ProductItemType}
              autoAspectRatio={`1/${autoAspectRatio}`}
            />
          )}
          responsive={[
            {
              breakpoint: 767, // screen width <= 768px
              settings: {
                slidesToShow: 2,
                slidesToScroll: 2,
              },
            },
          ]}
          useTransform
        />
      );
    }

    if (designSettings.layoutDisplay === 'bundle' && !!product) {
      const rebuildMainProduct = rebuildProductItemDataFromShopifyData(product);
      return (
        <RecommendationBundle
          style={designSettings.bundleStyle}
          productList={[rebuildMainProduct, ...state.products]}
        />
      );
    }

    return null;
  };

  useImperativeHandle(helpersRef, () => ({
    getWidgetData,
  }));

  return (
    <div className={clsNameMap.root()} ref={ref}>
      {(state.loading || !inView) && <LoadingIcon />}
      {inView && (
        <>
          {renderTitle()}
          {renderWidget()}
        </>
      )}
    </div>
  );
};

export default registryComponent('RecommendationWidget', RecommendationWidget);
